import { Disc3, Search, SearchX } from 'lucide-react'
import { useMemo } from 'react'
import { useNavigate } from 'react-router'

import { useLibraryAlbums } from '@/api/hooks'
import type { LibraryAlbum } from '@/api/types'
import { EmptyState } from '@/components/ui'
import { ErrorNotice } from '@/components/ui/Feedback'

import { errorMessage } from './errorMessage'
import { LibraryAlbumCard, LibraryAlbumCardSkeleton } from './LibraryAlbumCard'

const GRID = 'grid grid-cols-2 gap-1 sm:grid-cols-3 lg:grid-cols-4'

export interface LibraryAlbumGridProps {
  /** The debounced filter from the toolbar. */
  search: string
  onOpen: (album: LibraryAlbum) => void
  onClearSearch: () => void
}

/** A stable key: the Singles bucket has no id of its own. */
export function albumKey(album: LibraryAlbum): string {
  return album.id === null ? 'singles' : `album-${album.id}`
}

export function LibraryAlbumGrid({ search, onOpen, onClearSearch }: LibraryAlbumGridProps) {
  const navigate = useNavigate()
  const albums = useLibraryAlbums(search || undefined)

  // Singles last: it is a leftovers bucket, not a record. Everything else keeps
  // the order the server chose.
  const ordered = useMemo(() => {
    const results = albums.data?.results ?? []
    return [...results].sort((a, b) => Number(a.is_singles) - Number(b.is_singles))
  }, [albums.data])

  if (albums.isPending) {
    return (
      <ul className={GRID} role="status" aria-label="Loading albums">
        {Array.from({ length: 8 }, (_, position) => (
          <LibraryAlbumCardSkeleton key={position} />
        ))}
      </ul>
    )
  }

  if (albums.isError) {
    return (
      <ErrorNotice title="Could not load your albums" busy={albums.isFetching} onRetry={() => void albums.refetch()}>
        {errorMessage(albums.error)}
      </ErrorNotice>
    )
  }

  if (ordered.length === 0) {
    return search ? (
      <EmptyState
        icon={SearchX}
        title={`No album matches “${search}”`}
        description="Try the artist's name, or clear the filter to see everything you have."
        action={{ label: 'Clear filter', onClick: onClearSearch }}
      />
    ) : (
      <EmptyState
        icon={Disc3}
        title="No albums yet"
        description="Albums appear here when you add their songs to your library."
        action={{
          label: 'Search for music',
          icon: Search,
          onClick: () => {
            void navigate('/search')
          },
        }}
      />
    )
  }

  return (
    <ul className={GRID}>
      {ordered.map((album) => (
        <LibraryAlbumCard key={albumKey(album)} album={album} onOpen={onOpen} />
      ))}
    </ul>
  )
}
